import {
    compact as _compact,
    countBy as _countBy,
    groupBy as _groupBy,
    isNil as _isNil,
    maxBy as _maxBy,
    orderBy as _orderBy,
    sum as _sum,
    sumBy as _sumBy,
    uniq as _uniq,
} from 'lodash';
import {
    PurchaseOrder,
    PurchaseOrderLine,
    UnifiedQueryRequestV2,
} from 'src/app/shared/services';

// What each product was last bought for on 1688, and the offer page to buy it
// again, from two sources that are never mixed in storage:
//
// - Matched rows, management-console.1688_product_data. Written by
//   lnwshop-insight-functions/tools/sync-1688, run by hand and on no schedule.
//   A product not bought since the data starts has no row, and a price is what
//   was paid then, not today's. It excludes shipping and order-level coupons,
//   and there is no MOQ or price tier. The sync replaces whole documents, so
//   anything a person wrote into this collection would be lost on its next run.
//
// - What people set, the 'supplier-1688-overrides' document of the App Config
//   Store. The sync's database user can only write the collection above, so
//   nothing it does can touch these. They are laid over the rows when read.

// Prices and quantities are per the store's own selling unit unless named
// otherwise: costPerStoreUnitCny is unitPriceCny x packQty, where packQty is
// how many 1688 units make up one store unit.
export interface Supplier1688Row {
    pid: number;
    offerId: string;
    skuId: string | null;
    specLabel: string | null;
    offerTitle: string;
    url: string;
    sellerName: string;
    sellerUrl: string;
    unitPriceCny: number;
    packQty: number;
    costPerStoreUnitCny: number;
    part: string | null;
    lastOrderDate: string; // "YYYY-MM-DD"
    isLatest: boolean;
    matchConfidence: string;
    matchNote: string | null;
}

export const SUPPLIER_1688_REQUEST: UnifiedQueryRequestV2 = {
    db: 'management-console',
    col: '1688_product_data',
    // Every row, not just isLatest: the editor offers a product's earlier
    // purchases, and names shops from all of them.
    query: {},
    options: {
        // _id, title and sku stay out: rows are joined onto inventory rows,
        // which own those names (and _id here is a string, not the pid).
        projection: {
            _id: 0,
            pid: 1,
            offerId: 1,
            skuId: 1,
            specLabel: 1,
            offerTitle: 1,
            url: 1,
            sellerName: 1,
            sellerUrl: 1,
            unitPriceCny: 1,
            packQty: 1,
            costPerStoreUnitCny: 1,
            part: 1,
            lastOrderDate: 1,
            isLatest: 1,
            matchConfidence: 1,
            matchNote: 1,
        },
    },
};

export interface SupplierOverridePart {
    url: string;
    offerId: string;
    label: string;
    partName: string | null;
    packQty: number;
    // Per 1688 unit. Null follows this product's last order of this option.
    unitPriceCny: number | null;
}

export interface SupplierProductOverride {
    // Present: replaces the product's matched rows. Empty: the product is not
    // bought on 1688, so a matched row is a mistake. Absent: the rows stand.
    parts?: SupplierOverridePart[];
    shopUrl?: string | null;
    // Offers someone checked and found right. Held per offer, not as a flag on
    // the product, so an order from another offer gets looked at again.
    confirmedOffers?: string[];
    note?: string | null;
    updatedAt: string;
}

// What a caller saves; the service stamps when.
export type SupplierProductChange = Omit<SupplierProductOverride, 'updatedAt'>;

export interface SupplierOverridesDoc {
    products: Record<number, SupplierProductOverride>;
    // Shop URL to the name people read.
    shops: Record<string, string>;
}

export const EMPTY_SUPPLIER_OVERRIDES: SupplierOverridesDoc = {
    products: {},
    shops: {},
};

interface Supplier1688Part {
    offerId: string;
    url: string;
    // The option to pick on the offer page. The link opens the page without
    // selecting it. An offer with no options has no specLabel.
    label: string;
    // "ฐาน PF083A" and so on on the extra parts of a set; null on the main one.
    partName: string | null;
    offerTitle: string;
    packQty: number;
}

export interface Supplier1688Item {
    // Main part first.
    parts: Supplier1688Part[];
    // Every part's option, as one value the grid can sort, filter and export.
    label: string;
    source: 'matched' | 'user';
    shopName: string | null;
    shopUrl: string | null;
    // Null when a person set the offer and it has no price yet.
    costCny: number | null;
    lastOrderDate: string | null;
    // Any 1688 unit count differing from the store's.
    packed: boolean;
    needsReview: boolean;
    reviewNote: string | null;
    // A person looked at the match and said it is right.
    confirmed: boolean;
}

export interface SupplierShop {
    url: string;
    name: string;
    companyName: string | null;
    products: number;
}

// One load of 1688 data with everything derived from it. A save rebuilds it
// from the same rows and the document the save returned.
export interface Supplier1688Index {
    rows: Supplier1688Row[];
    overrides: SupplierOverridesDoc;
    byPid: Record<number, Supplier1688Item>;
    // Every shop a row or a person has mentioned, most used first.
    shops: SupplierShop[];
    // Newest order in the data, null when there is none.
    dataThrough: string | null;
}

export const EMPTY_SUPPLIER_INDEX: Supplier1688Index = {
    rows: [],
    overrides: EMPTY_SUPPLIER_OVERRIDES,
    byPid: {},
    shops: [],
    dataThrough: null,
};

// Stands in for a shop on a product with no 1688 data.
export const NO_SUPPLIER_KEY = 'no-1688-data';

const newestDate = (rows: Supplier1688Row[]): string =>
    _maxBy(rows, 'lastOrderDate')?.lastOrderDate ?? '';

// The URL identifies a shop. Its company name is not unique to it over time: a
// shop that was renamed appears under both names, so the newest order names it.
const companyNameByUrl = (rows: Supplier1688Row[]): Map<string, string> => {
    const names = new Map<string, string>();
    _orderBy(rows, 'lastOrderDate', 'asc').forEach((row) =>
        names.set(row.sellerUrl, row.sellerName),
    );
    return names;
};

// "vks888.1688.com" reads as a name. "shop1449680085886.1688.com" does not.
const shopNameFromUrl = (url: string): string | null => {
    const subdomain = /^https?:\/\/([^./]+)\.1688\.com/i.exec(url)?.[1];
    return subdomain && !/^shop.*\d/i.test(subdomain) ? subdomain : null;
};

// The option as a person reads it. An offer with no options has only its title.
export const optionLabel = (row: Supplier1688Row): string =>
    row.specLabel ?? row.offerTitle;

const joinLabels = (parts: { label: string }[]): string =>
    parts.map((part) => part.label).join(' + ');

const matchedItem = (
    rows: Supplier1688Row[],
    shopName: (url: string) => string,
    override: SupplierProductOverride | undefined,
): Supplier1688Item => {
    const ordered = _orderBy(rows, (row) => row.part ?? '', 'asc');
    const main = ordered[0];
    const confirmedOffers = override?.confirmedOffers ?? [];
    const confirmed =
        confirmedOffers.length > 0 &&
        ordered.every((row) => confirmedOffers.includes(row.offerId));
    const parts = ordered.map((row) => ({
        offerId: row.offerId,
        url: row.url,
        label: optionLabel(row),
        partName: row.part,
        offerTitle: row.offerTitle,
        packQty: row.packQty,
    }));
    return {
        parts,
        label: joinLabels(parts),
        source: 'matched',
        shopName: shopName(main.sellerUrl),
        shopUrl: main.sellerUrl,
        costCny: _sumBy(ordered, 'costPerStoreUnitCny'),
        lastOrderDate: newestDate(ordered),
        packed: ordered.some((row) => row.packQty > 1),
        needsReview:
            !confirmed &&
            ordered.some((row) => row.matchConfidence === 'review'),
        reviewNote: ordered.find((row) => row.matchNote)?.matchNote ?? null,
        confirmed,
    };
};

// The last price paid for this product for this option, which keeps following
// new orders while a person's link stands. Nothing less specific will do: one
// offer holds options at different prices, and the parts of a set are often two
// options of one offer. A label someone reworded has no price until they enter
// one, which beats showing another option's.
export const lastPaidForOption = (
    productRows: Supplier1688Row[],
    offerId: string,
    label: string,
): Supplier1688Row | null =>
    _maxBy(
        productRows.filter(
            (row) =>
                row.offerId === offerId &&
                optionLabel(row).trim() === label.trim(),
        ),
        'lastOrderDate',
    ) ?? null;

const userItem = (
    override: SupplierProductOverride,
    productRows: Supplier1688Row[],
    shopName: (url: string) => string,
): Supplier1688Item => {
    const overrideParts = override.parts ?? [];
    const lastPaid = overrideParts.map((part) =>
        lastPaidForOption(productRows, part.offerId, part.label),
    );
    const costs = overrideParts.map((part, index) => {
        const unitPrice = part.unitPriceCny ?? lastPaid[index]?.unitPriceCny;
        return unitPrice === undefined ? null : unitPrice * part.packQty;
    });
    const parts = overrideParts.map((part) => ({
        offerId: part.offerId,
        url: part.url,
        label: part.label,
        partName: part.partName,
        offerTitle: part.label,
        packQty: part.packQty,
    }));
    return {
        parts,
        label: joinLabels(parts),
        source: 'user',
        shopName: override.shopUrl ? shopName(override.shopUrl) : null,
        shopUrl: override.shopUrl ?? null,
        // A partial sum would read as the price of the whole set.
        costCny: costs.includes(null) ? null : _sum(costs),
        lastOrderDate: newestDate(_compact(lastPaid)) || null,
        packed: parts.some((part) => part.packQty > 1),
        needsReview: false,
        reviewNote: override.note ?? null,
        confirmed: false,
    };
};

export const buildSupplierIndex = (
    rows: Supplier1688Row[],
    overrides: SupplierOverridesDoc = EMPTY_SUPPLIER_OVERRIDES,
): Supplier1688Index => {
    const companyNames = companyNameByUrl(rows);
    // What to call a shop: the name someone set, then the shop's own address,
    // and the company name only as a last resort. That one is Chinese, which
    // the people ordering cannot read.
    const shopName = (url: string): string =>
        overrides.shops[url] ??
        shopNameFromUrl(url) ??
        companyNames.get(url) ??
        url;
    const rowsByPid = _groupBy(rows, 'pid');
    const byPid: Record<number, Supplier1688Item> = {};
    Object.entries(rowsByPid).forEach(([pid, productRows]) => {
        const latest = productRows.filter((row) => row.isLatest);
        if (latest.length > 0) {
            byPid[Number(pid)] = matchedItem(
                latest,
                shopName,
                overrides.products[Number(pid)],
            );
        }
    });
    Object.entries(overrides.products).forEach(([pid, override]) => {
        if (!override.parts) {
            return;
        }
        if (override.parts.length === 0) {
            delete byPid[Number(pid)];
            return;
        }
        byPid[Number(pid)] = userItem(override, rowsByPid[pid] ?? [], shopName);
    });
    const productCounts = _countBy(Object.values(byPid), 'shopUrl');
    const shopUrls = _uniq([
        ...companyNames.keys(),
        ...Object.keys(overrides.shops),
        // A shop only products point at has lost its name, not its products.
        ..._compact(
            Object.values(overrides.products).map((entry) => entry.shopUrl),
        ),
    ]);
    const shops = _orderBy(
        shopUrls.map((url) => ({
            url,
            name: shopName(url),
            companyName: companyNames.get(url) ?? null,
            products: productCounts[url] ?? 0,
        })),
        ['products', 'name'],
        ['desc', 'asc'],
    );
    return {
        rows,
        overrides,
        byPid,
        shops,
        dataThrough: newestDate(rows) || null,
    };
};

export type SupplierStatus =
    'check' | 'matched' | 'confirmed' | 'user' | 'not-on-1688' | 'missing';

export const supplierStatus = (
    item: Supplier1688Item | undefined,
    override: SupplierProductOverride | undefined,
): SupplierStatus => {
    if (override?.parts?.length === 0) {
        return 'not-on-1688';
    }
    if (!item) {
        return 'missing';
    }
    if (item.source === 'user') {
        return 'user';
    }
    if (item.needsReview) {
        return 'check';
    }
    return item.confirmed ? 'confirmed' : 'matched';
};

const OFFER_ID = /^\d{6,}$/;
const OFFER_PATH_LINK =
    /^(?:https?:\/\/)?(?:[a-z0-9-]+\.)*1688\.com\/(?:[^?#]*\/)?offer\/(\d{6,})/i;
const OFFER_QUERY_LINK =
    /^(?:https?:\/\/)?(?:[a-z0-9-]+\.)*1688\.com\/.*[?&]offerId=(\d{6,})/i;

// The offer id out of what someone pasted: a full link with or without its
// query string, a mobile link, or the bare id. The stored link is rebuilt from
// the id, so only an offer page ever becomes a link in the app.
export const parseOfferLink = (
    text: string | null | undefined,
): { offerId: string; url: string } | null => {
    const trimmed = (text ?? '').trim();
    const offerId =
        OFFER_ID.exec(trimmed)?.[0] ??
        OFFER_PATH_LINK.exec(trimmed)?.[1] ??
        OFFER_QUERY_LINK.exec(trimmed)?.[1];
    return offerId
        ? { offerId, url: `https://detail.1688.com/offer/${offerId}.html` }
        : null;
};

// 1688's own hosts, where a pasted offer or search link lands. No shop is there.
const NON_SHOP_SUBDOMAINS = [
    'air',
    'detail',
    'dj',
    'login',
    'm',
    'offer',
    'page',
    's',
    'trade',
    'work',
    'www',
];

// A shop's address from what someone pasted, trimmed to scheme and host.
export const parseShopLink = (
    text: string | null | undefined,
): string | null => {
    const subdomain = /^(?:https?:\/\/)?([a-z0-9-]+)\.1688\.com(?:[/?#]|$)/i
        .exec((text ?? '').trim())?.[1]
        .toLowerCase();
    return subdomain && !NON_SHOP_SUBDOMAINS.includes(subdomain)
        ? `https://${subdomain}.1688.com`
        : null;
};

// What to enter on 1688 for this many store units. Parts of a set are normally
// bought one for one; if they ever differ, each count is shown.
export const supplierOrderQtyText = (
    item: Supplier1688Item | null | undefined,
    qty: number,
): string =>
    item
        ? _uniq(item.parts.map((part) => part.packQty * qty))
              .map((count) => count.toLocaleString())
              .join(' / ')
        : '';

interface OrderLineView {
    line: PurchaseOrderLine;
    // Keyed by the line's pid rather than read off a grid row: an order may
    // hold a product that is hidden now and so has no row, and it is still
    // bought.
    supplier: Supplier1688Item | null;
    qtyText: string;
}

interface OrderShopGroup {
    // Both null on the group of lines with no 1688 data.
    shopName: string | null;
    shopUrl: string | null;
    lines: OrderLineView[];
    estimateCny: number;
    // Lines the estimate leaves out for having no price, so it can say so.
    unpricedLines: number;
}

export interface OrderSupplierView {
    groups: OrderShopGroup[];
    shopCount: number;
    estimateCny: number;
    unpricedLines: number;
}

// An order's lines as a list to buy from, one shop at a time: 1688 checks out
// per shop. Largest shop first, lines without data last, and within a shop the
// lines of one offer page sit together so a page is opened once.
export const buildOrderSupplierView = (
    order: PurchaseOrder,
    byPid: Record<number, Supplier1688Item>,
): OrderSupplierView => {
    // The stored document is not validated, so an order may have no lines.
    const lines = (order.lines ?? []).map((line) => {
        const supplier = byPid[line.pid] ?? null;
        return {
            line,
            supplier,
            qtyText: supplierOrderQtyText(supplier, line.qty),
        };
    });
    const byShop = _groupBy(
        lines,
        (entry) => entry.supplier?.shopUrl ?? NO_SUPPLIER_KEY,
    );
    const groups: OrderShopGroup[] = _orderBy(
        Object.values(byShop).map((shopLines) => ({
            shopName: shopLines[0].supplier?.shopName ?? null,
            shopUrl: shopLines[0].supplier?.shopUrl ?? null,
            lines: _orderBy(
                shopLines,
                (entry) => entry.supplier?.parts[0].offerId ?? '',
            ),
            estimateCny: _sumBy(
                shopLines,
                (entry) => (entry.supplier?.costCny ?? 0) * entry.line.qty,
            ),
            unpricedLines: shopLines.filter((entry) =>
                _isNil(entry.supplier?.costCny),
            ).length,
        })),
        [
            (group) => (group.shopUrl ? 0 : 1),
            (group) => group.lines.length,
            'shopName',
        ],
        ['asc', 'desc', 'asc'],
    );
    return {
        groups,
        shopCount: groups.filter((group) => group.shopUrl).length,
        estimateCny: _sumBy(groups, 'estimateCny'),
        unpricedLines: _sumBy(groups, 'unpricedLines'),
    };
};
